import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { useDraggable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';
import { spacing1x } from '../../../styles/spacing.js';
import { getColorWithOpacity } from '../../../utils/color.js';
import { useDndDroppableContext } from '../DndDroppableV2/DndDroppableV2.js';
import { jsx } from 'react/jsx-runtime';

var DndDropPlaceholder = styled.div.withConfig({
  displayName: "DndDraggableV2__DndDropPlaceholder",
  componentId: "sc-13rtsoj-0"
})(["&&{border-radius:", ";", ";}&& .dnd-drop-placeholder-children{opacity:0;}"], spacing1x, function (_ref) {
  var theme = _ref.theme;
  return "\n        background: ".concat(getColorWithOpacity(theme.CTAColors.primary, 0.1), ";\n        border: 1px dashed ").concat(theme.CTAColors.primary, ";\n    ");
});
function DndDraggableV2(props) {
  var children = props.children,
    connectedTo = props.connectedTo,
    dragData = props.dragData,
    draggableId = props.draggableId,
    dragOverlay = props.dragOverlay,
    dropPlaceholder = props.dropPlaceholder,
    style = props.style;
  var droppableContext = useDndDroppableContext();
  if (droppableContext !== null && droppableContext !== void 0 && droppableContext.direction) {
    return /*#__PURE__*/jsx(DndDraggableSortable, {
      connectedTo: connectedTo,
      dragData: dragData,
      draggableId: draggableId,
      dragOverlay: dragOverlay,
      dropPlaceholder: dropPlaceholder,
      style: style,
      children: children
    });
  }
  return /*#__PURE__*/jsx(DndDraggableDraggable, {
    connectedTo: connectedTo,
    dragData: dragData,
    draggableId: draggableId,
    dragOverlay: dragOverlay,
    dropPlaceholder: dropPlaceholder,
    style: style,
    children: children
  });
}
DndDraggableV2.displayName = "DndDraggableV2";
function DndDraggableDraggable(props) {
  var children = props.children,
    connectedTo = props.connectedTo,
    dragData = props.dragData,
    draggableId = props.draggableId,
    dragOverlay = props.dragOverlay,
    dropPlaceholder = props.dropPlaceholder,
    style = props.style;
  var droppableContext = useDndDroppableContext();
  var _useDraggable = useDraggable({
      id: draggableId,
      data: {
        connectedTo: connectedTo,
        dragData: dragData,
        dragOverlay: dragOverlay || (typeof children === "function" ? children({}) : children),
        droppableMode: droppableContext === null || droppableContext === void 0 ? void 0 : droppableContext.mode
      }
    }),
    attributes = _useDraggable.attributes,
    isDragging = _useDraggable.isDragging,
    listeners = _useDraggable.listeners,
    setNodeRef = _useDraggable.setNodeRef,
    transform = _useDraggable.transform;
  return /*#__PURE__*/jsx(DndDraggableContent, {
    attributes: attributes,
    dropPlaceholder: dropPlaceholder,
    isDragging: isDragging,
    listeners: listeners,
    setNodeRef: setNodeRef,
    style: style,
    transform: transform,
    children: children
  });
}
DndDraggableDraggable.displayName = "DndDraggableDraggable";
function DndDraggableSortable(props) {
  var children = props.children,
    connectedTo = props.connectedTo,
    dragData = props.dragData,
    draggableId = props.draggableId,
    dragOverlay = props.dragOverlay,
    dropPlaceholder = props.dropPlaceholder,
    style = props.style;
  var droppableContext = useDndDroppableContext();
  var _useSortable = useSortable({
      id: draggableId,
      data: {
        connectedTo: connectedTo,
        dragData: dragData,
        dragOverlay: dragOverlay || (typeof children === "function" ? children({}) : children),
        droppableMode: droppableContext === null || droppableContext === void 0 ? void 0 : droppableContext.mode
      }
    }),
    attributes = _useSortable.attributes,
    isDragging = _useSortable.isDragging,
    listeners = _useSortable.listeners,
    setNodeRef = _useSortable.setNodeRef,
    transform = _useSortable.transform,
    transition = _useSortable.transition;
  return /*#__PURE__*/jsx(DndDraggableContent, {
    attributes: attributes,
    dropPlaceholder: dropPlaceholder,
    isDragging: isDragging,
    listeners: listeners,
    setNodeRef: setNodeRef,
    style: style,
    transform: transform,
    transition: transition,
    children: children
  });
}
DndDraggableSortable.displayName = "DndDraggableSortable";
function DndDraggableContent(props) {
  var attributes = props.attributes,
    children = props.children,
    dropPlaceholder = props.dropPlaceholder,
    isDragging = props.isDragging,
    listeners = props.listeners,
    setNodeRef = props.setNodeRef,
    style = props.style,
    transform = props.transform,
    transition = props.transition;
  var droppableContext = useDndDroppableContext();
  var itemStyle = _objectSpread(_objectSpread({}, style), {}, {
    cursor: typeof children === "function" ? "unset" : "grab",
    transform: CSS.Translate.toString(transform),
    transition: transition
  });
  var content = typeof children === "function" ? children({
    dragHandleProps: _objectSpread(_objectSpread({}, attributes), listeners),
    isDragging: isDragging
  }) : children;
  if (isDragging) {
    if (dropPlaceholder) {
      return dropPlaceholder;
    } else if (droppableContext !== null && droppableContext !== void 0 && droppableContext.direction) {
      content = /*#__PURE__*/jsx(DndDropPlaceholder, {
        children: /*#__PURE__*/jsx("div", {
          className: "dnd-drop-placeholder-children",
          children: content
        })
      });
    } else if ((droppableContext === null || droppableContext === void 0 ? void 0 : droppableContext.mode) === "copy") {
      return content;
    } else {
      return /*#__PURE__*/jsx("div", {
        style: _objectSpread(_objectSpread({}, itemStyle), {}, {
          opacity: 0
        }),
        children: content
      });
    }
  }
  return /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({
    style: itemStyle,
    ref: setNodeRef
  }, typeof children === "function" ? {} : _objectSpread(_objectSpread({}, attributes), listeners)), {}, {
    children: content
  }));
}
DndDraggableContent.displayName = "DndDraggableContent";

export { DndDraggableV2 };
